/**
 * # SegmentedControl
 *
 * A component for selecting between mutually exclusive options, displayed as a horizontal group of buttons.
 * Useful for toggling between views, modes, or filter options.
 *
 * @see [Design ticket]{@link https://github.com/albert-io/nexus/issues/15199}
 *
 * ## Props
 *
 * - `options: OptionObject[] | ReactNode[]` - Array of options to display. Can be either:
 *   - Objects with `value`, `label`, and optional `disabled` properties
 *   - JSX elements for custom button content
 * - `onSelect?: (value: string | number) => void` - Callback fired when an option is selected.
 *   - When using `OptionObject[]`, receives the selected option's `value`
 *   - When using `ReactNode[]`, receives the selected option's index
 * - `className?: string` (optional) - Additional CSS class name for the container.
 * - `defaultSelectedIdx?: number` - Index of the initially selected option. Default is `0`.
 * - `disabled?: boolean` - Whether the entire control is disabled. Default is `false`.
 * - `disabledIndices?: number[]` - Array of indices to disable individually (for ReactNode options).
 *
 * ## Usage
 *
 * ### With value/label options
 *
 * ```tsx
 * import SegmentedControl from './SegmentedControl';
 *
 * const ViewToggle = () => {
 *   return (
 *     <SegmentedControl
 *       options={[
 *         { value: 'grid', label: 'Grid View' },
 *         { value: 'list', label: 'List View' },
 *         { value: 'table', label: 'Table View', disabled: true }
 *       ]}
 *       onSelect={(value) => console.log('Selected:', value)}
 *       defaultSelectedIdx={0}
 *     />
 *   );
 * };
 * ```
 *
 * ### With custom JSX elements
 *
 * ```tsx
 * import SegmentedControl from './SegmentedControl';
 * import { Icon } from '@albert-io/atomic';
 *
 * const LanguageToggle = () => {
 *   return (
 *     <SegmentedControl
 *       options={[
 *         <span key="en">English <Icon icon="check" /></span>,
 *         <span key="es">Spanish</span>
 *       ]}
 *       onSelect={(idx) => console.log('Selected index:', idx)}
 *       disabledIndices={[1]} // Disable Spanish option
 *     />
 *   );
 * };
 * ```
 *
 * ### Disabled state (entire control)
 *
 * ```tsx
 * <SegmentedControl
 *   options={[
 *     { value: 'on', label: 'On' },
 *     { value: 'off', label: 'Off' }
 *   ]}
 *   disabled
 * />
 * ```
 *
 * ### Individual disabled options
 *
 * ```tsx
 * <SegmentedControl
 *   options={[
 *     { value: 'free', label: 'Free' },
 *     { value: 'pro', label: 'Pro', disabled: true },
 *     { value: 'enterprise', label: 'Enterprise' }
 *   ]}
 * />
 * ```
 *
 * ### Toggle mode
 *
 * When `toggleMode` is `true` and there are exactly 2 options, clicking anywhere
 * on the control toggles between them (instead of requiring a click on a specific button).
 * The container renders with `role="switch"` and `aria-checked` for accessibility.
 *
 * ```tsx
 * <SegmentedControl
 *   options={[
 *     { value: 'all', label: 'All' },
 *     { value: 'selected', label: 'Selected' }
 *   ]}
 *   toggleMode
 *   onSelect={(value) => console.log('Toggled to:', value)}
 * />
 * ```
 */

import Button from '@albert-io/atomic/atoms/Button/Button.react';
import classNames from 'classnames';
import type {ReactNode} from 'react';
import React, {isValidElement, useState} from 'react';

import './segmented-control.scss';

interface OptionObject {
  value: string | number;
  label: ReactNode;
  disabled?: boolean;
}

interface SegmentedControlProps {
  options: OptionObject[] | ReactNode[];
  onSelect?: (value: string | number) => void;
  className?: string;
  /** Additional CSS class name for the button elements */
  buttonClassName?: string;
  defaultSelectedIdx?: number;
  /**
   * Controlled selected index. When provided, the component becomes controlled
   * and the visual selection is driven entirely by this prop.
   * Use together with `onSelect` to update the value.
   */
  selectedIdx?: number;
  /** Disables the entire control */
  disabled?: boolean;
  /** Array of indices to disable individually (for ReactNode options) */
  disabledIndices?: number[];
  // whether or not to change this component's background color to a darker shade of the parent
  blendDark?: boolean;
  /** When true and there are exactly 2 options, clicking anywhere toggles between them */
  toggleMode?: boolean;
}

const SegmentedControl = ({
  options,
  onSelect,
  className,
  buttonClassName,
  defaultSelectedIdx = 0,
  selectedIdx,
  disabled = false,
  disabledIndices = [],
  blendDark = false,
  toggleMode = false
}: SegmentedControlProps) => {
  const isControlled = selectedIdx !== undefined;
  const [internalIdx, setInternalIdx] = useState(defaultSelectedIdx);
  const selectedOptionIdx = isControlled ? selectedIdx : internalIdx;
  const setSelectedOptionIdx = isControlled ? () => {} : setInternalIdx;
  const isToggle = toggleMode && options.length === 2;

  const handleToggle = () => {
    if (disabled) return;
    const nextIdx = selectedOptionIdx === 0 ? 1 : 0;
    const nextOption = options[nextIdx];
    const isNextDisabled = isValidElement(nextOption)
      ? disabledIndices.includes(nextIdx)
      : (nextOption as OptionObject).disabled || disabledIndices.includes(nextIdx);
    if (isNextDisabled) return;
    setSelectedOptionIdx(nextIdx);
    if (isValidElement(nextOption)) {
      onSelect?.(nextIdx);
    } else {
      onSelect?.((nextOption as OptionObject).value);
    }
  };

  return (
    <div
      className={classNames('segmented-control', className, {
        'segmented-control--disabled': disabled,
        'segmented-control--blend-dark': blendDark,
        'cursor-pointer': isToggle && !disabled
      })}
      onClick={isToggle ? handleToggle : undefined}
      role={isToggle ? 'switch' : undefined}
      aria-checked={isToggle ? selectedOptionIdx === 1 : undefined}
    >
      {options.map((option, idx) => {
        // If it's a JSX element, render it directly
        if (isValidElement(option)) {
          const isItemDisabled = disabled || disabledIndices.includes(idx);

          return (
            <Button
              // oxlint-disable-next-line react/no-array-index-key -- fixed-order segmented-control options; JSX-element options have no stable id
              key={idx}
              aria-pressed={isToggle ? undefined : selectedOptionIdx === idx}
              color='secondary'
              variant='solid'
              size='s'
              disabled={isItemDisabled}
              onClick={
                isToggle
                  ? undefined
                  : () => {
                      if (!isItemDisabled) {
                        setSelectedOptionIdx(idx);
                        onSelect?.(idx);
                      }
                    }
              }
              className={classNames('segmented-control_btn', buttonClassName, {
                'group segmented-control_btn--selected': selectedOptionIdx === idx,
                'pointer-events-none': isToggle
              })}
            >
              {option}
            </Button>
          );
        }

        // Otherwise, treat it as an OptionObject
        const {value, label, disabled: optionDisabled} = option as OptionObject;
        const isItemDisabled = disabled || optionDisabled || disabledIndices.includes(idx);

        const onClick = isToggle
          ? undefined
          : () => {
              if (!isItemDisabled) {
                onSelect?.(value);
                setSelectedOptionIdx(idx);
              }
            };

        return (
          <Button
            key={value}
            aria-pressed={isToggle ? undefined : selectedOptionIdx === idx}
            color='secondary'
            variant='solid'
            size='s'
            onClick={onClick}
            className={classNames('segmented-control_btn', buttonClassName, {
              'segmented-control_btn--selected': selectedOptionIdx === idx,
              'pointer-events-none': isToggle
            })}
            disabled={isItemDisabled}
          >
            {isValidElement(option) ? option : label}
          </Button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
